import { useAuthContext } from '@/auth/useAuthContext';
import { ApiError, getSmsProvider, removeSmsProvider, upsertSmsProvider } from '@/lib/api';
import type { SmsProviderState, SmsProviderUpsertInput } from '@/lib/types';
import { AlertCircle, CheckCircle2, Phone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Spec §11.5 — SMS + WhatsApp (Twilio) onboarding UI.
//
// State-machine renderer keyed on `state.twilioStatus`:
//   - null / 'not_configured'      → 5-step setup walkthrough (POST).
//   - 'verified'                   → connected card + Disconnect (DELETE).
//   - 'invalid_credentials'        → re-verify prompt + form to re-enter.
//   - 'suspended'                  → contact-support card (no actions).
//
// Pro+ only. Starter tenants see a greyed upgrade CTA on the same route.
// ---------------------------------------------------------------------------

const PRO_TIERS = new Set(['pro', 'enterprise']);

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

function SetupForm({
  onSaved,
}: {
  onSaved: (state: SmsProviderState) => void;
}) {
  const [accountSid, setAccountSid] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [messagingServiceSid, setMessagingServiceSid] = useState('');
  const [whatsappSenderId, setWhatsappSenderId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!accountSid.trim() || !authToken.trim() || !messagingServiceSid.trim()) {
      setError('All fields except WhatsApp sender are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const input: SmsProviderUpsertInput = {
        accountSid: accountSid.trim(),
        authToken: authToken.trim(),
        messagingServiceSid: messagingServiceSid.trim(),
        whatsappSenderId: whatsappSenderId.trim() ? whatsappSenderId.trim() : null,
      };
      const state = await upsertSmsProvider(input);
      onSaved(state);
    } catch (err) {
      if (err instanceof ApiError) {
        // 422 → verification_failed; 503 → encryption_not_configured;
        // 400 → invalid_body. Show the code; the page reads like a setup
        // checklist so the bare code is enough for the operator.
        const reason = (err.body as { reason?: string } | null)?.reason;
        setError(reason ? `${err.code}: ${reason}` : err.code);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      <div>
        <label
          htmlFor="account-sid"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Account SID
        </label>
        <input
          id="account-sid"
          type="text"
          value={accountSid}
          onChange={(e) => setAccountSid(e.target.value)}
          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
          disabled={saving}
        />
      </div>
      <div>
        <label
          htmlFor="auth-token"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Auth Token
        </label>
        <input
          id="auth-token"
          type="password"
          value={authToken}
          onChange={(e) => setAuthToken(e.target.value)}
          placeholder="••••••••••••••••••••••••••••••••"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
          disabled={saving}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          We encrypt this with AES-256-GCM before storing. Never visible after save.
        </p>
      </div>
      <div>
        <label
          htmlFor="messaging-service-sid"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Messaging Service SID
        </label>
        <input
          id="messaging-service-sid"
          type="text"
          value={messagingServiceSid}
          onChange={(e) => setMessagingServiceSid(e.target.value)}
          placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
          disabled={saving}
        />
      </div>
      <div>
        <label
          htmlFor="whatsapp-sender-id"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          WhatsApp Sender ID <span className="lowercase text-muted-foreground">(optional)</span>
        </label>
        <input
          id="whatsapp-sender-id"
          type="text"
          value={whatsappSenderId}
          onChange={(e) => setWhatsappSenderId(e.target.value)}
          placeholder="whatsapp:+447xxxxxxxxx"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
          disabled={saving}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Leave blank if you don't yet have a WhatsApp Business sender approved by Meta.
        </p>
      </div>
      <button
        type="submit"
        disabled={saving}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
      >
        {saving ? 'Verifying with Twilio…' : 'Save and verify'}
      </button>
    </form>
  );
}

function DisconnectButton({ onRemoved }: { onRemoved: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);

  async function handleClick() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setRemoving(true);
    try {
      await removeSmsProvider();
    } catch {
      // ignore — caller will reload state
    }
    onRemoved();
  }

  return (
    <button
      type="button"
      onClick={() => void handleClick()}
      disabled={removing}
      className="rounded-md border border-destructive/40 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 press disabled:opacity-50"
    >
      {confirming ? 'Confirm disconnect' : 'Disconnect'}
    </button>
  );
}

function SetupWalkthrough({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-6">
      <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside">
        <li>
          Sign up at{' '}
          <a
            href="https://www.twilio.com/try-twilio"
            target="_blank"
            rel="noreferrer noopener"
            className="text-primary underline"
          >
            twilio.com
          </a>{' '}
          and verify your business email + phone.
        </li>
        <li>
          Complete{' '}
          <a
            href="https://help.twilio.com/articles/1260801864489-Trust-Hub-Customer-Profile-Set-Up"
            target="_blank"
            rel="noreferrer noopener"
            className="text-primary underline"
          >
            carrier registration
          </a>{' '}
          (A2P 10DLC in the US; sender ID approval elsewhere).
        </li>
        <li>
          Create a{' '}
          <a
            href="https://console.twilio.com/us1/develop/sms/services"
            target="_blank"
            rel="noreferrer noopener"
            className="text-primary underline"
          >
            Messaging Service
          </a>{' '}
          and attach your phone number(s).
        </li>
        <li>
          (Optional) Apply for a{' '}
          <a
            href="https://www.twilio.com/whatsapp"
            target="_blank"
            rel="noreferrer noopener"
            className="text-primary underline"
          >
            WhatsApp Business sender
          </a>{' '}
          and have your message templates pre-approved by Meta.
        </li>
        <li>
          Paste your Account SID, Auth Token, and Messaging Service SID below — we'll verify them
          against Twilio before saving.
        </li>
      </ol>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function SmsProvider() {
  const { state: authState } = useAuthContext();
  const [state, setState] = useState<SmsProviderState | null>(null);
  const [loading, setLoading] = useState(true);

  const tier = authState.me?.activeAccount?.tier ?? 'starter';
  const isPro = PRO_TIERS.has(tier);

  function load() {
    setLoading(true);
    getSmsProvider()
      .then((s) => setState(s))
      .catch(() => {
        // non-fatal — keep last state
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!isPro) {
      setLoading(false);
      return;
    }
    load();
  }, [isPro]);

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-8 w-48 animate-pulse rounded bg-secondary" />
        <div className="h-4 w-full animate-pulse rounded bg-secondary" />
      </div>
    );
  }

  const status = state?.twilioStatus ?? 'not_configured';

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">
          SMS / WhatsApp provider
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect your own Twilio account to send SMS and WhatsApp messages to customers. We pass
          through your messages — your Twilio account is billed directly.
        </p>
      </header>

      {!isPro ? (
        <section
          className="rounded-2xl bg-surface ring-1 ring-border elevate opacity-80"
          data-testid="upgrade-card"
        >
          <div className="border-b border-border px-6 py-4">
            <h2 className="flex items-center gap-2 text-sm font-medium">
              <Phone className="size-4 text-muted-foreground" strokeWidth={1.75} />
              Upgrade to Pro to enable SMS + WhatsApp
            </h2>
          </div>
          <div className="px-6 py-5 space-y-4 text-sm text-muted-foreground">
            <p>
              SMS and WhatsApp delivery is included in the Pro and Enterprise tiers. Bring your own
              Twilio account — we pass through your messages, you pay Twilio directly for usage.
            </p>
            <Link
              to="/settings/billing"
              className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
            >
              Upgrade your plan
            </Link>
          </div>
        </section>
      ) : (
        <div className="space-y-6">
          {status === 'not_configured' && (
            <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
              <div className="border-b border-border px-6 py-4">
                <h2 className="flex items-center gap-2 text-sm font-medium">
                  <Phone className="size-4 text-muted-foreground" strokeWidth={1.75} />
                  Connect Twilio
                </h2>
              </div>
              <div className="px-6 py-5">
                <SetupWalkthrough>
                  <SetupForm onSaved={(s) => setState(s)} />
                </SetupWalkthrough>
              </div>
            </section>
          )}

          {status === 'verified' && state && (
            <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
              <div className="border-b border-border px-6 py-4">
                <h2 className="flex items-center gap-2 text-sm font-medium">
                  <CheckCircle2 className="size-4 text-success" strokeWidth={1.75} />
                  Connected to Twilio
                </h2>
              </div>
              <div className="px-6 py-5 space-y-4">
                <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Account SID</dt>
                  <dd className="font-mono">{state.twilioAccountSidMasked}</dd>
                  <dt className="text-muted-foreground">Messaging Service</dt>
                  <dd className="font-mono">{state.twilioMessagingServiceSid}</dd>
                  <dt className="text-muted-foreground">WhatsApp sender</dt>
                  <dd className="font-mono">
                    {state.whatsappSenderId ?? (
                      <span className="text-muted-foreground italic">not configured</span>
                    )}
                  </dd>
                  {state.twilioVerifiedAt && (
                    <>
                      <dt className="text-muted-foreground">Verified at</dt>
                      <dd className="num">
                        {new Date(state.twilioVerifiedAt).toLocaleString('en-GB')}
                      </dd>
                    </>
                  )}
                </dl>
                <DisconnectButton onRemoved={() => load()} />
              </div>
            </section>
          )}

          {status === 'invalid_credentials' && (
            <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
              <div className="border-b border-border px-6 py-4">
                <h2 className="flex items-center gap-2 text-sm font-medium">
                  <AlertCircle className="size-4 text-destructive" strokeWidth={1.75} />
                  Twilio credentials no longer working
                </h2>
              </div>
              <div className="px-6 py-5 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Your saved credentials stopped working — likely because the auth token was rotated
                  in Twilio. Customer SMS / WhatsApp messages are paused. Re-enter your credentials
                  to reconnect.
                </p>
                <SetupForm onSaved={(s) => setState(s)} />
              </div>
            </section>
          )}

          {status === 'suspended' && (
            <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
              <div className="border-b border-border px-6 py-4">
                <h2 className="flex items-center gap-2 text-sm font-medium">
                  <AlertCircle className="size-4 text-destructive" strokeWidth={1.75} />
                  Twilio integration suspended
                </h2>
              </div>
              <div className="px-6 py-5 space-y-3 text-sm text-muted-foreground">
                <p>
                  Your Twilio integration has been suspended by the platform. Customer SMS /
                  WhatsApp messages are paused.
                </p>
                <p>Contact platform support to resolve.</p>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
